import { action } from '@ember/object';
import Component from '@glimmer/component';

import type { CustomPeriodPickerOptions, PopupData } from 'webapp-types';
import type { AttachPosition, Theme } from 'webapp/enums/enums';

import FormRangePopupComponent from 'webapp/components/form/range/popup';
import FormSelectWithPopupAsDropdownComponent from 'webapp/components/form/select-with-popup-as-dropdown';

import DateRange from 'webapp/models-custom/date-range';
import type MetricLimitations from 'webapp/models-custom/metric-limitations';

interface Args {
	metricLimitations: MetricLimitations;
	onChange: (dateRange: DateRange) => void;
	theme?: Theme | string;
	size?: string;
	placeholder?: string;
	isDisabled?: boolean;
	skipAllTime?: boolean;
	exceptionIds?: number[];
	trackingOrigin?: string;
	popupSkipAttachToCheck?: boolean;
	customPeriodPickerOptions?: CustomPeriodPickerOptions;
	popupOptions?: {
		skipAttachToCheck?: boolean;
		offset?: number;
		attachPosition?: AttachPosition;
		alignEdgeWithAttachTo?: boolean;
	};
}

export default class FormRangeDropdownComponent extends Component<Args> {
	get dateRange() {
		return this.args.metricLimitations.dateRange;
	}

	// ------ for select dropdown ----- //

	get selected() {
		return this.dateRange?.rangeUISelectionId;
	}

	get options() {
		// only need to update options when either range text has changed
		// or range title has changed (only for lastX)

		const limitations = this.args.metricLimitations.limitationsForSelectedRange;

		let unsupportedLabel = '';
		if (limitations?.disabled) {
			unsupportedLabel = `<ui-icon name="AlertFill" size="sm" class="warn" title="${limitations.infoText}"></ui-icon>`;
		}

		const options = DateRange.predefinedRanges.map((range) => {
			return {
				value: range.rangeUISelectionId,
				// since we will only see the currently selected option we can show same title on all for easier logic
				label: this.dateRange?.rangeTitleUX || range.rangeTitle,
				label3: unsupportedLabel,
			};
		});

		return options;
	}

	get extraClass() {
		// only show icon when no placeholder given
		return this.args.placeholder ? '' : 'daterange';
	}

	@action
	doNothing() {
		// changes on this dropdown will go through the popup
		// since the dropdown cannot even be opened
	}

	// ------ for popup ----- //

	popupClass = 'popup-cdr popup-cdr-range';

	popupComponent = FormRangePopupComponent;

	get showAllTime() {
		return !this.args.skipAllTime;
	}

	get exceptionIds() {
		// won't be showing these categories in the popup
		return this.args.exceptionIds || [];
	}

	@action
	getPopupProps() {
		const exceptionIds = Array.from(this.exceptionIds);

		if (!exceptionIds.includes(DateRange.Category.POPULAR) || !this.showAllTime) {
			// all time is already shown in the popular section
			if (!exceptionIds.includes(DateRange.Category.ALLTIME)) {
				exceptionIds.push(DateRange.Category.ALLTIME);
			}
		}

		const props: PopupData.Form.Range = {
			metricLimitations: this.args.metricLimitations,
			exceptionIds,
			showAllTime: this.showAllTime,
			trackingOrigin: this.args.trackingOrigin,
			onChange: this.args.onChange,
			customPeriodPickerOptions: this.args.customPeriodPickerOptions,
		};

		return props;
	}
	<template>
		<FormSelectWithPopupAsDropdownComponent
			@selected={{this.selected}}
			@options={{this.options}}
			@onChange={{this.doNothing}}
			@isNumeric={{false}}
			@placeholder={{@placeholder}}
			@isDisabled={{@isDisabled}}
			@theme={{@theme}}
			@size={{@size}}
			@popupComponent={{this.popupComponent}}
			@popupClass={{this.popupClass}}
			@popupOffset={{@popupOptions.offset}}
			@popupSkipAttachToCheck={{@popupOptions.skipAttachToCheck}}
			@popupAttachPosition={{@popupOptions.attachPosition}}
			@popupalignEdgeWithAttachTo={{@popupOptions.alignEdgeWithAttachTo}}
			@getPopupProps={{this.getPopupProps}}
		/>
	</template>
}
